import React, { useState } from 'react';
import {
    CheckCircle2,
    AlertCircle,
    Loader2,
    ShieldCheck,
    FileSpreadsheet,
    ArrowRight,
    X
} from 'lucide-react';
import { forensicService } from '../../services/api';
import { toast } from 'sonner';
import { FileDropzone } from '../ui/FileDropzone';
import { ForensicPartialBanner, forensicNameError, HOSTED_FORENSIC_NOTE } from './forensicUpload';

interface ForensicIngestionModalProps {
    isOpen: boolean;
    onClose: () => void;
    caseId: string;
    investigationId?: string;
    onSuccess?: (recordId: string) => void;
}

export const ForensicIngestionModal: React.FC<ForensicIngestionModalProps> = ({
    isOpen,
    onClose,
    caseId,
    investigationId,
    onSuccess
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [step, setStep] = useState<'select' | 'validate' | 'upload'>('select');
    const [isValidating, setIsValidating] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [validationResult, setValidationResult] = useState<any>(null);
    const [notes, setNotes] = useState('');
    const [fileError, setFileError] = useState<string | null>(null);

    if (!isOpen) return null;

    const runValidation = async () => {
        if (!file) return;
        setIsValidating(true);
        try {
            const result = await forensicService.validate(caseId, file);
            setValidationResult(result);
            setStep('validate');
        } catch (error: any) {
            toast.error(error.response?.data?.detail || 'Validation failed');
        } finally {
            setIsValidating(false);
        }
    };

    const runUpload = async () => {
        if (!file) return;
        setIsUploading(true);
        try {
            const result = await forensicService.upload(caseId, file, investigationId, notes);
            if (result.success) {
                toast.success('Forensic record ingested successfully');
                onSuccess?.(result.record_id);
                onClose();
            } else {
                toast.error(result.error || 'Ingestion failed');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.detail || 'Upload failed');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 text-foreground">
            <div className="w-full max-w-[550px] rounded-xl border border-border bg-card shadow-xl overflow-hidden flex flex-col">
                <div className="p-5 border-b border-border flex justify-between items-start">
                    <div>
                        <h3 className="text-lg font-bold flex items-center gap-2">
                            <ShieldCheck className="w-5 h-5 text-primary" />
                            Forensic Record Ingestion
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">
                            Upload exchange subpoena responses (Binance), a freeform wallet list, or a Cellebrite UFDR / EnCase device extraction.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <a
                            href="/api/v1/forensic/templates/freeform.csv"
                            download
                            className="text-[10px] uppercase tracking-wider font-bold text-primary hover:text-primary px-2 py-1 rounded border border-primary/30 hover:border-blue-500/60 transition-colors"
                            title="Download freeform CSV template"
                        >
                            CSV Template
                        </a>
                        <button onClick={onClose} className="p-1 hover:bg-muted rounded-md transition-colors">
                            <X className="w-4 h-4 text-muted-foreground" />
                        </button>
                    </div>
                </div>

                <div className="p-6 space-y-4">
                    {step === 'select' && (
                        <div className="space-y-4">
                            <FileDropzone
                                bare
                                accept=".xlsx,.xls,.csv,.ufdr,.e01,.zip,.ab"
                                file={file}
                                onFileChange={(f) => {
                                    const err = f ? forensicNameError(f.name) : null;
                                    setFileError(err);
                                    if (err) {
                                        toast.error(err);
                                        setFile(null);
                                    } else {
                                        setFile(f);
                                    }
                                    setStep('select');
                                    setValidationResult(null);
                                }}
                                title="Exchange / device export"
                                description=".xlsx · .csv · .ufdr · .zip · .ab. .e01 is the offline build."
                            />
                            {fileError && (
                                <p role="alert" data-testid="forensic-file-error" className="text-[11px] text-red-500 leading-relaxed">
                                    {fileError}
                                </p>
                            )}

                            <div className="bg-primary/5 border border-primary/20 rounded-md p-3 flex gap-3">
                                <AlertCircle className="h-5 w-5 text-primary shrink-0" />
                                <div className="space-y-1">
                                    <p className="text-xs font-bold text-primary uppercase tracking-tight">What to upload?</p>
                                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                                        Original exchange subpoena response (extracts <strong>KYC</strong>, <strong>Deposit</strong>, <strong>Withdrawal</strong>),
                                        a freeform <strong>wallet CSV</strong>, or a Cellebrite <strong>.ufdr</strong> (8 examination methods including OCR + WhatsApp/Telegram database decoders extract address candidates). {HOSTED_FORENSIC_NOTE}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 'validate' && validationResult && (
                        <div className="space-y-4 animate-in fade-in duration-300">
                            <ForensicPartialBanner partial={validationResult.partial} reason={validationResult.partial_reason} />
                            {validationResult.success ? (
                                <div className="space-y-2">
                                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-md p-3 flex gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                                        <div className="space-y-1 flex-1">
                                            <p className="text-xs font-bold text-emerald-500 uppercase tracking-tight">
                                                Recognised as: {validationResult.parser ?? validationResult.format ?? 'unknown'}
                                            </p>
                                            {validationResult.customer && (
                                                <p className="text-[11px] text-muted-foreground">
                                                    Customer: <strong>{validationResult.customer.name || '(not in file)'}</strong>
                                                    {validationResult.customer.email && <> · {validationResult.customer.email}</>}
                                                    {validationResult.customer.exchange_user_id && <> · UID {validationResult.customer.exchange_user_id}</>}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    {typeof validationResult.movements_total === 'number' && (
                                        <div className="grid grid-cols-3 gap-2">
                                            <div className="bg-background border border-border rounded p-2">
                                                <p className="text-[9px] text-muted-foreground uppercase font-bold">Movements</p>
                                                <p className="text-base font-bold tabular-nums">{validationResult.movements_total}</p>
                                            </div>
                                            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded p-2">
                                                <p className="text-[9px] text-emerald-500/80 uppercase font-bold">On-chain hash</p>
                                                <p className="text-base font-bold tabular-nums text-emerald-500">{validationResult.movements_with_hash ?? 0}</p>
                                            </div>
                                            <div className="bg-amber-500/5 border border-amber-500/20 rounded p-2">
                                                <p className="text-[9px] text-amber-500/80 uppercase font-bold">Internal-id only</p>
                                                <p className="text-base font-bold tabular-nums text-amber-500">{validationResult.movements_internal_id_only ?? 0}</p>
                                            </div>
                                        </div>
                                    )}
                                    {validationResult.warnings && validationResult.warnings.length > 0 && (
                                        <div className="bg-amber-500/5 border border-amber-500/20 rounded p-2">
                                            <p className="text-[10px] font-bold text-amber-500 uppercase mb-1">{validationResult.warnings.length} warning(s)</p>
                                            <ul className="text-[10px] text-muted-foreground space-y-0.5 max-h-20 overflow-y-auto">
                                                {validationResult.warnings.slice(0, 5).map((w: string, i: number) => (
                                                    <li key={i} className="truncate">· {w}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="bg-red-500/10 border border-red-500/20 rounded-md p-3 flex gap-3">
                                    <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
                                    <div className="space-y-1">
                                        <p className="text-xs font-bold text-red-500 uppercase tracking-tight">Compatibility Issue</p>
                                        <p className="text-[11px] text-muted-foreground">
                                            {validationResult.error}
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Internal Notes (Optional)</label>
                                <textarea
                                    className="w-full min-h-[80px] p-3 rounded-md border border-border bg-background text-sm focus:ring-1 focus:ring-primary focus:outline-none transition-all"
                                    placeholder="e.g. Received via Email from HTX Compliance on 2026-04-18"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </div>

                            <div className="bg-muted/30 p-3 rounded-md border border-border/50">
                                <p className="text-[10px] font-bold flex items-center gap-1.5 text-foreground uppercase tracking-tight">
                                    <ShieldCheck className="w-3.5 h-3.5 text-primary" /> Data Retention Policy
                                </p>
                                <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">
                                    Every detail in this file (including KYC, IP logs, and internal User IDs) will be stored securely in the case evidence locker for future lead generation.
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-5 border-t border-border bg-muted/20 flex gap-2">
                    {step === 'select' ? (
                        <button 
                            onClick={runValidation} 
                            disabled={!file || isValidating}
                            className="w-full h-10 flex items-center justify-center gap-2 bg-primary text-primary-foreground font-bold text-sm rounded-md hover:opacity-90 disabled:opacity-50 transition-all shadow-sm"
                        >
                            {isValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                            Audit File
                        </button>
                    ) : (
                        <>
                            <button 
                                onClick={() => setStep('select')}
                                className="h-10 px-4 border border-border text-foreground font-bold text-sm rounded-md hover:bg-muted transition-colors"
                            >
                                Back
                            </button>
                            <button 
                                onClick={runUpload} 
                                disabled={isUploading || !validationResult?.success}
                                className="flex-1 h-10 flex items-center justify-center gap-2 bg-primary text-primary-foreground font-bold text-sm rounded-md hover:bg-brand-gold-hover disabled:opacity-50 transition-all shadow-sm"
                            >
                                {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                                Complete Ingestion
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
